import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  InlineInputCardNameWrapper,
  InlineInputCardWrapper,
  InputDescriptionWrapper,
} from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/shared';
import { toCssDeclarationName } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/css-declarations';
import { NumberUnitInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/number-unit-input';
import { SingleSelectInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/single-select-input';
import { DropdownInputOption } from '@/features/editor/widgets/custom-widget/inputs/shared/dropdown';
import { ColorSelectInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/shared/color-select-input';
import { CircularCheckmark } from '@/components/circular-checkmark';
import {
  parseBorderValue,
  hasImportant,
  stripImportant,
  addImportantIfNeeded,
} from '@/features/editor/widgets/shared/ai-editing-layout/inputs/parsers';
import { useOnChange } from '@/components/hooks/use-on-change';

const BORDER_STYLE_OPTIONS: DropdownInputOption[] = [
  { label: `None`, value: `none` },
  { label: `Solid`, value: `solid` },
  { label: `Dashed`, value: `dashed` },
  { label: `Dotted`, value: `dotted` },
  { label: `Double`, value: `double` },
  { label: `Groove`, value: `groove` },
  { label: `Ridge`, value: `ridge` },
  { label: `Inset`, value: `inset` },
  { label: `Outset`, value: `outset` },
];

export interface CssBorderInputProps {
  name: string;
  initialValue?: string;
  onChange?: (name: string, value: string) => void;
}

export function CssBorderInput({
  name,
  initialValue,
  onChange,
}: CssBorderInputProps) {
  const displayName = useMemo(() => toCssDeclarationName(name), [name]);

  // Track if initial value had !important
  const [hadImportant] = useState(() => hasImportant(initialValue || ``));
  const cleanInitialValue = useMemo(
    () => stripImportant(initialValue || ``),
    [initialValue],
  );

  const [isEnabled, setIsEnabled] = React.useState(
    cleanInitialValue !== `none` &&
      cleanInitialValue !== `0` &&
      cleanInitialValue !== `0px`,
  );
  const defaultValues = useMemo(() => {
    if (
      !cleanInitialValue ||
      cleanInitialValue === `none` ||
      cleanInitialValue === `0` ||
      cleanInitialValue === `0px`
    ) {
      return { width: `1px`, style: `solid`, color: `rgba(0, 0, 0, 1)` };
    }
    return parseBorderValue(cleanInitialValue);
  }, [cleanInitialValue]);

  const [width, setWidth] = React.useState(defaultValues.width || `1px`);
  const [style, setStyle] = React.useState(defaultValues.style || `solid`);
  const [color, setColor] = React.useState(
    defaultValues.color || `rgba(0, 0, 0, 1)`,
  );

  const border = useMemo(() => {
    if (!isEnabled) return addImportantIfNeeded(`none`, hadImportant);
    return addImportantIfNeeded(`${width} ${style} ${color}`, hadImportant);
  }, [width, style, color, isEnabled, hadImportant]);

  useOnChange(() => {
    onChange?.(name, border);
  }, [border]);

  return (
    <InlineInputCardWrapper>
      <InlineInputCardNameWrapper>
        <CircularCheckmark
          square
          selected={isEnabled}
          onChange={setIsEnabled}
        />
        {` `}
        {displayName}
      </InlineInputCardNameWrapper>
      <ControlsWrapper className={isEnabled ? `` : `disabled`}>
        <InputDescriptionWrapper>
          <SingleSelectInput
            options={BORDER_STYLE_OPTIONS}
            initialValue={
              BORDER_STYLE_OPTIONS.find((opt) => opt.value === style) ||
              BORDER_STYLE_OPTIONS[0]
            }
            onChange={(v) => setStyle(v.value)}
          />
          <span style={{ transform: `translateY(-0.5rem)`, zIndex: 1 }}>
            Style
          </span>
        </InputDescriptionWrapper>
        <InputDescriptionWrapper className="offset">
          <NumberUnitInput initialValue={width} onChange={setWidth} />
          <span>Width</span>
        </InputDescriptionWrapper>
        <InputDescriptionWrapper style={{ marginLeft: `0.5rem` }}>
          <ColorSelectInput
            initialValue={color}
            onChange={setColor}
            showHeader={false}
          />
          <span>Color</span>
        </InputDescriptionWrapper>
      </ControlsWrapper>
    </InlineInputCardWrapper>
  );
}

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 2rem;

  &.disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  transition: opacity 0.2s ease-in-out;
`;
